/**
 * Shared Card Description Component
 *
 * Renders the description section of a result card with a sparkle icon
 * and an accordion "Read more / Show less" toggle for long text.
 */

import { h } from 'preact';
import { useState } from 'preact/hooks';
import htm from 'htm';

const html = htm.bind(h);

/**
 * Strips basic Markdown formatting and normalizes whitespace in descriptions.
 *
 * @param {string} text - Raw text with markdown.
 * @returns {string} Clean plain text.
 */
function cleanDescriptionText(text) {
  if (!text) return '';
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [link text](url) -> link text
    .replace(/\*\*([^*]+)\*\*/g, '$1')       // **bold** -> bold
    .replace(/\*([^*]+)\*/g, '$1')           // *italic* -> italic
    .replace(/__([^_]+)__/g, '$1')           // __bold__ -> bold
    .replace(/_([^_]+)_/g, '$1')             // _italic_ -> italic
    .replace(/\\r\\n|\r\n|\\n|\n/g, ' ')     // newlines -> space
    .replace(/\s{2,}/g, ' ')                 // multiple spaces -> single space
    .trim();
}

/**
 * Truncates text and provides a "Read more" accordion toggle.
 *
 * @param {Object} props
 * @param {string} props.text - The description text to display.
 * @param {Object} props.ctx - View context containing `t` (i18n helper).
 * @param {number} [props.maxLength=120] - Maximum visible characters before truncation.
 * @param {string} [props.variant] - Visual variant ('card' or 'popup').
 */
function AccordionText({ text, ctx, maxLength = 120, variant }) {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  const cleanText = cleanDescriptionText(text);

  if (cleanText.length <= maxLength) {
    return html`<span class="mta-card-desc-text">${cleanText}</span>`;
  }

  const isPopup = variant === 'popup';

  if (isPopup) {
    return html`
      <div class="mta-card-desc-text mta-map-desc-content">
        <div class=${`mta-map-desc-scrollable ${expanded ? 'is-expanded' : ''}`}>
          ${cleanText}
        </div>
        <button
          type="button"
          onClick=${(e) => {
            e.preventDefault();
            setExpanded(!expanded);
          }}
          class="mta-read-more-btn mta-map-expand-btn"
        >
          ${expanded ? (ctx?.t?.('show_less') || 'Show less') : (ctx?.t?.('read_more') || 'Read more')}
        </button>
      </div>
    `;
  }

  const displayText = expanded ? cleanText : cleanText.substring(0, maxLength) + '...';

  return html`
    <span class="mta-card-desc-text">
      ${displayText}
      <button
        type="button"
        onClick=${(e) => {
          e.preventDefault();
          setExpanded(!expanded);
        }}
        class="mta-read-more-btn"
      >
        ${expanded ? (ctx?.t?.('show_less') || 'Show less') : (ctx?.t?.('read_more') || 'Read more')}
      </button>
    </span>
  `;
}

/**
 * Card description section with sparkle icon and accordion text.
 *
 * @param {Object} props
 * @param {string} props.description - The description text.
 * @param {Object} props.ctx - View context containing `t` (i18n helper).
 * @param {number} [props.maxLength=120] - Maximum visible characters before truncation.
 * @param {string} [props.variant] - Visual variant ('card' or 'popup').
 */
export function CardDescription({ description, ctx, maxLength = 120, variant }) {
  if (!description) return null;

  return html`
    <div class="mta-card-desc-row">
      <span class="mta-card-sparkle">\u2726</span>
      <div style="flex:1; min-width:0;">
        <${AccordionText} text=${description} ctx=${ctx} maxLength=${maxLength} variant=${variant} />
      </div>
    </div>
  `;
}
